import {
  createSelector,
  createSlice,
  type PayloadAction,
} from '@reduxjs/toolkit'
import { all, put, takeLatest } from 'redux-saga/effects'
import { v4 as uuid } from 'uuid'
import type { ApiError, Nil } from '@pbt/pbt-ui-components'

import type { SoapForm } from '~/types'

import * as API from '../../api'
import { getErrorMessage } from '../../utils/errors'
import type { RootState } from '../index'
import requestAPI from '../sagas/utils/requestAPI'

export const FETCH_DOCUMENTS_LIST_V2 = 'questions/FETCH_DOCUMENTS_LIST_V2'
export const FETCH_DOCUMENTS_LIST_V2_SUCCESS =
  'questions/FETCH_DOCUMENTS_LIST_V2_SUCCESS'
export const FETCH_DOCUMENTS_LIST_V2_FAILURE =
  'questions/FETCH_DOCUMENTS_LIST_V2_FAILURE'

export type DocumentsV2State = {
  error: string | null
  isLoading: boolean
  list: SoapForm[]
}

export const INITIAL_STATE: DocumentsV2State = {
  list: [],
  error: null,
  isLoading: false,
}

const documentsV2Slice = createSlice({
  name: 'documentsV2',
  initialState: INITIAL_STATE,
  reducers: {
    fetchDocumentsListV2: (
      state,
      _action: PayloadAction<{
        appointmentId: string
      }>,
    ) => {
      state.isLoading = true
      state.error = null
      state.list = []
    },
    fetchDocumentsListV2Success: (
      state,
      action: PayloadAction<{
        data: SoapForm[]
      }>,
    ) => {
      state.list = action.payload.data
      state.isLoading = false
    },
    fetchDocumentsListV2Failure: (state, action: PayloadAction<ApiError>) => {
      state.error = getErrorMessage(action.payload)
      state.isLoading = false
    },
  },
})

export const documentsV2Reducer = documentsV2Slice.reducer

export const {
  fetchDocumentsListV2,
  fetchDocumentsListV2Success,
  fetchDocumentsListV2Failure,
} = documentsV2Slice.actions

export const getDocumentsV2 = (state: RootState) => state.documentsV2
export const getDocumentsV2List = (state: RootState): SoapForm[] =>
  getDocumentsV2(state).list
export const getDocumentsV2ByIds = (
  clientId: string | Nil,
  patientId: string | Nil,
) =>
  createSelector(getDocumentsV2List, (list) =>
    list.filter(
      (doc) =>
        (!clientId || doc.clientId === clientId) &&
        (!patientId || doc.patientId === patientId),
    ),
  )
export const getDocumentsV2IsLoading = (state: RootState): boolean =>
  getDocumentsV2(state).isLoading
export const getDocumentsV2Error = (state: RootState): string | null =>
  getDocumentsV2(state).error

export function* sagaFetchDocumentsListV2({
  payload: { appointmentId },
}: ReturnType<typeof fetchDocumentsListV2>) {
  try {
    const list = yield* requestAPI(API.fetchDocumentsListV2, appointmentId)
    const listWithIds = list.map((item: SoapForm) => ({
      ...item,
      id: item.id || uuid(),
      generatedId: Boolean(item.id),
    }))
    yield put(fetchDocumentsListV2Success({ data: listWithIds }))
  } catch (error) {
    yield put(fetchDocumentsListV2Failure(error as ApiError))
  }
}

function* watchFetchDocumentsList() {
  yield takeLatest(fetchDocumentsListV2.type, sagaFetchDocumentsListV2)
}

export function* documentsV2Saga() {
  yield all([watchFetchDocumentsList()])
}
